import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    LifeBuoy, Plus, RefreshCw, Send, ChevronRight, UserCheck, Download, MessageSquareReply, Inbox, Clock,
    Star, Lock, CheckCircle2, RotateCcw, XCircle, AlertTriangle, Users, Activity,
} from "lucide-react";
import api from "../../utils/api";
import useCurrentUser from "../../hooks/useCurrentUser";
import {
    CATEGORIES, STATUSES, PRIORITIES, IMPACT, URGENCY, STAGES, SLA_HOURS, DONE_STATUSES,
    categoryOf, statusOf, priorityOf, ticketNumber, needsReply, slaState, fmtDateTime, apiError,
} from "./ticketConfig";
import {
    PageHeader, Button, KpiTile, ObjectStatus, Avatar, SearchField, SelectField, Field, TableCard,
    EmptyState, MessageStrip, Dialog, surface, inputCls, thCls, tdCls, relTime, TONES,
} from "./fiori";

const isBreached = t => !DONE_STATUSES.has(t.status) && !!(t.sla?.response?.breached || t.sla?.resolution?.breached);

function TicketStatus({ status, customer }) {
    const s = statusOf(status);
    return <ObjectStatus tone={s.tone} icon={s.icon}>{(customer && s.customer) || s.label}</ObjectStatus>;
}
function PriorityBadge({ p }) {
    const pr = PRIORITIES[p] || PRIORITIES[3];
    return <ObjectStatus tone={pr.tone}>{pr.label}</ObjectStatus>;
}
function SlaChip({ clock, label }) {
    const s = slaState(clock);
    return (
        <span className="inline-flex items-center gap-1 text-xs" title={clock?.due ? `${label} target: ${fmtDateTime(clock.due)}` : ""}>
            <span className={`w-1.5 h-1.5 rounded-full ${TONES[s.tone].dot}`} />
            <span className="text-slate-500 dark:text-slate-400">{label}</span>
            <span className={s.tone === "negative" ? "text-red-600 dark:text-red-300 font-semibold" : s.tone === "critical" ? "text-amber-600 dark:text-amber-300 font-semibold" : "text-slate-600 dark:text-slate-300"}>{s.text}</span>
        </span>
    );
}
function Progress({ value }) {
    return (
        <div className="h-1.5 w-20 rounded-full bg-slate-100 dark:bg-white/[0.06] overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-[#00B7EB] to-[#0065A0]" style={{ width: `${value}%` }} />
        </div>
    );
}

// Customer tickets — a ServiceNow-style service desk.
// mode="staff": Hasbro customer care (all customers; status, priority, assignment, work notes).
// mode="customer": a customer login (its own customer's tickets only — enforced by the backend).
export default function TicketDesk({ mode }) {
    const staff = mode === "staff";
    const { user } = useCurrentUser();
    const [tickets, setTickets] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [view, setView] = useState("open");          // open | done | all  (+ staff: mine | unassigned | breached)
    // ?customer=<id> opens the desk filtered to one customer (from Customer Accounts)
    const [filters, setFilters] = useState(() => ({ category: "", customerId: new URLSearchParams(window.location.search).get("customer") || "", priority: "", q: "" }));
    const [openId, setOpenId] = useState(null);
    const [showNew, setShowNew] = useState(false);

    const { category, customerId } = filters;
    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const params = Object.fromEntries(Object.entries({ category, customerId }).filter(([, v]) => v));
            const { data } = await api.get("/api/hasbro/tickets", { params });
            setTickets(data.tickets || []);
        } catch (err) {
            setError(apiError(err, "Could not load tickets."));
        } finally {
            setLoading(false);
        }
    }, [category, customerId]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (staff) api.get("/api/hasbro/customers").then(r => setCustomers(r.data.customers || [])).catch(() => {});
    }, [staff]);

    const VIEWS = staff
        ? [["open", "Open"], ["mine", "Assigned to me"], ["unassigned", "Unassigned"], ["breached", "SLA breached"], ["done", "Resolved & closed"], ["all", "All"]]
        : [["open", "Open"], ["done", "Resolved & closed"], ["all", "All"]];
    const uid = user?.uid;

    const shown = useMemo(() => {
        const q = filters.q.trim().toLowerCase();
        const inView = t => ({
            open: !DONE_STATUSES.has(t.status), done: DONE_STATUSES.has(t.status), all: true,
            mine: t.assignedTo?.uid === uid && !DONE_STATUSES.has(t.status),
            unassigned: !t.assignedTo && !DONE_STATUSES.has(t.status), breached: isBreached(t),
        })[view];
        return tickets.filter(t => inView(t)
            && (!filters.priority || String(t.priority) === filters.priority)
            && (!q || [ticketNumber(t.number), `#${t.number}`, t.subject, t.reference, t.customerName, t.createdBy?.name, t.assignedTo?.name].some(v => (v || "").toLowerCase().includes(q))))
            // most urgent first, then most recently updated
            .sort((a, b) => (DONE_STATUSES.has(a.status) - DONE_STATUSES.has(b.status)) || (a.priority - b.priority) || (b.updatedAt || "").localeCompare(a.updatedAt || ""));
    }, [tickets, filters, view, uid]);

    if (openId) return <TicketObjectPage id={openId} staff={staff} onBack={() => { setOpenId(null); load(); }} />;

    const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));
    const open = tickets.filter(t => !DONE_STATUSES.has(t.status));
    const count = id => tickets.filter(t => t.status === id).length;
    const replyCount = tickets.filter(needsReply).length;

    const exportCsv = () => {
        const cols = ["Number", "Subject", "Customer", "Category", "Reference", "Priority", "Status", "Assigned to", "Opened by", "Opened", "Response", "Resolution", "Rating"];
        const rows = shown.map(t => [ticketNumber(t.number), t.subject, t.customerName, categoryOf(t.category).label, t.reference || "", PRIORITIES[t.priority]?.label, statusOf(t.status).label,
            t.assignedTo?.name || "", t.createdBy?.name || "", t.createdAt || "", slaState(t.sla?.response).text, slaState(t.sla?.resolution).text, t.csat?.rating ?? ""]);
        const csv = [cols, ...rows].map(r => r.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        a.download = `customer-tickets-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    return (
        <div className="max-w-7xl mx-auto px-1 sm:px-2 py-4 space-y-5">
            <PageHeader
                breadcrumb={staff ? [{ label: "Hasbro" }, { label: "Customer care" }] : [{ label: "Hasbro" }, { label: "Support" }]}
                icon={LifeBuoy}
                title={staff ? "Customer Tickets" : "My Tickets"}
                subtitle={staff
                    ? "Every customer request with its priority, owner and service targets."
                    : "Raise as many tickets as you need — each one is tracked separately, with who's working on it and when to expect an answer."}
                actions={<>
                    <Button variant="transparent" icon={RefreshCw} onClick={load} title="Refresh" className={loading ? "[&>svg]:animate-spin" : ""} />
                    {staff && <Button icon={Download} onClick={exportCsv} disabled={!shown.length}>Export</Button>}
                    <Button variant="emphasized" icon={Plus} onClick={() => setShowNew(true)}>{staff ? "New ticket" : "Raise a ticket"}</Button>
                </>}
            >
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    <KpiTile label="Open" icon={LifeBuoy} tone="information" value={open.length} total={tickets.length} active={view === "open"} onClick={() => setView("open")} />
                    {staff
                        ? <KpiTile label="Unassigned" icon={Users} tone="critical" value={open.filter(t => !t.assignedTo).length} total={open.length} active={view === "unassigned"} onClick={() => setView("unassigned")} />
                        : <KpiTile label="Awaiting your reply" icon={MessageSquareReply} tone="indication" value={count("awaiting_customer")} total={open.length} onClick={() => setView("open")} />}
                    <KpiTile label="P1 / P2 open" icon={AlertTriangle} tone="negative" value={open.filter(t => t.priority <= 2).length} total={open.length} onClick={() => { setView("open"); set("priority", ""); }} />
                    <KpiTile label="SLA breached" icon={Clock} tone="negative" value={tickets.filter(isBreached).length} total={open.length} active={view === "breached"} onClick={() => staff && setView("breached")} />
                    <KpiTile label="Resolved & closed" icon={CheckCircle2} tone="positive" value={count("resolved") + count("closed")} total={tickets.length} active={view === "done"} onClick={() => setView("done")} />
                </div>
            </PageHeader>

            {staff && replyCount > 0 && (
                <MessageStrip type="warning">
                    <span className="font-semibold">{replyCount} ticket{replyCount !== 1 ? "s" : ""} waiting for a reply from Hasbro.</span>
                </MessageStrip>
            )}
            {error && <MessageStrip type="error">{error}</MessageStrip>}

            {/* View tabs + filter bar */}
            <div className={`${surface} overflow-hidden`}>
                <div className="flex gap-1 px-3 pt-2 border-b border-slate-100 dark:border-white/[0.06] overflow-x-auto">
                    {VIEWS.map(([id, label]) => (
                        <button key={id} onClick={() => setView(id)}
                            className={`relative px-3.5 py-2.5 text-sm font-semibold whitespace-nowrap ${view === id ? "text-[#0065A0] dark:text-[#6FD0F5]" : "text-slate-500 dark:text-slate-400 hover:text-[#1D2D3E] dark:hover:text-white"}`}>
                            {label}
                            {view === id && <span className="absolute left-2 right-2 bottom-0 h-[3px] rounded-t bg-[#0087C9]" />}
                        </button>
                    ))}
                </div>
                <div className="p-4 flex flex-wrap items-end gap-3">
                    <Field label="Search">
                        <SearchField value={filters.q} onChange={v => set("q", v)} placeholder="Number, subject, PO, person…" className="w-64 max-w-full" />
                    </Field>
                    {staff && (
                        <SelectField label="Customer" value={filters.customerId} onChange={v => set("customerId", v)} className="w-48">
                            <option value="">All customers</option>
                            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </SelectField>
                    )}
                    <SelectField label="Category" value={filters.category} onChange={v => set("category", v)} className="w-52">
                        <option value="">All categories</option>
                        {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </SelectField>
                    <SelectField label="Priority" value={filters.priority} onChange={v => set("priority", v)} className="w-44">
                        <option value="">All priorities</option>
                        {Object.entries(PRIORITIES).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
                    </SelectField>
                    {(filters.q || filters.category || filters.customerId || filters.priority) && (
                        <Button variant="transparent" onClick={() => setFilters({ category: "", customerId: "", priority: "", q: "" })} className="ml-auto">Clear filters</Button>
                    )}
                </div>
            </div>

            <TableCard title="Tickets" count={shown.length}>
                {!loading && shown.length === 0 ? (
                    tickets.length === 0
                        ? <EmptyState icon={Inbox} title="No tickets yet"
                            text={staff ? "When a customer raises a question it shows up here. You can also log one for them." : "Raise a ticket and Hasbro customer care will pick it up. You can have as many open at once as you need."}
                            action={<Button variant="emphasized" icon={Plus} onClick={() => setShowNew(true)}>{staff ? "New ticket" : "Raise a ticket"}</Button>} />
                        : <EmptyState icon={Inbox} title="Nothing in this view" text="Try another tab, a different search, or clear the filters." />
                ) : (
                    <div className="overflow-auto max-h-[65vh]">
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 z-10">
                                <tr>
                                    <th className={thCls}>Ticket</th>
                                    {staff && <th className={thCls}>Customer</th>}
                                    <th className={thCls}>Priority</th>
                                    <th className={thCls}>Status</th>
                                    <th className={thCls}>Assigned to</th>
                                    <th className={thCls}>Service targets</th>
                                    <th className={thCls}>Updated</th>
                                    <th className={`${thCls} w-8`} />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-white/[0.05]">
                                {loading && tickets.length === 0 && [0, 1, 2].map(i => (
                                    <tr key={i}><td colSpan={8} className="px-4 py-4"><div className="h-4 rounded bg-slate-100 dark:bg-white/[0.05] animate-pulse" /></td></tr>
                                ))}
                                {shown.map(t => {
                                    const cat = categoryOf(t.category);
                                    const reply = staff && needsReply(t);
                                    return (
                                        <tr key={t.id} onClick={() => setOpenId(t.id)} className="group cursor-pointer hover:bg-[#0087C9]/[0.04] dark:hover:bg-white/[0.03] transition-colors">
                                            <td className={`${tdCls} relative`}>
                                                <span className={`absolute left-0 top-2 bottom-2 w-[3px] rounded-r ${isBreached(t) ? "bg-red-500" : reply ? "bg-amber-500" : "bg-transparent"}`} />
                                                <p className="font-semibold text-[#1D2D3E] dark:text-white">
                                                    <span className="font-mono text-xs text-slate-400 font-medium mr-1.5">{ticketNumber(t.number)}</span>{t.subject}
                                                </p>
                                                <p className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                                    <cat.icon size={12} /> {cat.label}{t.reference && <span className="font-mono">· {t.reference}</span>}
                                                    {reply && <span className="ml-1.5 inline-flex items-center gap-1 text-amber-600 dark:text-amber-300 font-semibold"><MessageSquareReply size={12} /> Customer replied</span>}
                                                </p>
                                            </td>
                                            {staff && <td className={tdCls}><span className="flex items-center gap-2 text-slate-700 dark:text-slate-200 whitespace-nowrap"><Avatar name={t.customerName} size={24} square /> {t.customerName}</span></td>}
                                            <td className={tdCls}><PriorityBadge p={t.priority} /></td>
                                            <td className={tdCls}>
                                                <TicketStatus status={t.status} customer={!staff} />
                                                <div className="mt-1.5"><Progress value={t.completion} /></div>
                                            </td>
                                            <td className={tdCls}>
                                                {t.assignedTo
                                                    ? <span className="flex items-center gap-2 text-slate-600 dark:text-slate-300 whitespace-nowrap"><Avatar name={t.assignedTo.name} size={22} /> {t.assignedTo.name}</span>
                                                    : <span className="text-xs text-slate-400">{t.assignmentGroup} queue</span>}
                                            </td>
                                            <td className={tdCls}>
                                                <div className="flex flex-col gap-0.5">
                                                    <SlaChip clock={t.sla?.response} label="Response" />
                                                    <SlaChip clock={t.sla?.resolution} label="Resolution" />
                                                </div>
                                            </td>
                                            <td className={`${tdCls} text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap`} title={fmtDateTime(t.updatedAt)}>{relTime(t.updatedAt)}</td>
                                            <td className={`${tdCls} text-slate-300 group-hover:text-[#0087C9]`}><ChevronRight size={16} /></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </TableCard>

            {showNew && (
                <NewTicketDialog staff={staff} customers={customers} onClose={() => setShowNew(false)}
                    onCreated={t => { setShowNew(false); setOpenId(t.id); }} />
            )}
        </div>
    );
}

function ChoiceCards({ options, value, onChange }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {options.map(o => (
                <button key={o.value} type="button" onClick={() => onChange(o.value)}
                    className={`text-left px-3 py-2.5 rounded-lg border transition ${value === o.value
                        ? "border-[#0087C9] bg-[#0087C9]/10" : "border-slate-200 dark:border-white/10 hover:border-slate-300"}`}>
                    <span className={`block text-xs font-semibold ${value === o.value ? "text-[#0065A0] dark:text-[#6FD0F5]" : "text-slate-700 dark:text-slate-200"}`}>{o.label}</span>
                    <span className="block text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{o.hint}</span>
                </button>
            ))}
        </div>
    );
}

function NewTicketDialog({ staff, customers, onClose, onCreated }) {
    const [form, setForm] = useState({ customerId: "", category: "order", subject: "", reference: "", description: "", impact: 2, urgency: 2 });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
    const priority = priorityOf(form.impact, form.urgency);
    const sla = SLA_HOURS[priority];

    const submit = async () => {
        setSaving(true);
        setError("");
        try {
            const { data } = await api.post("/api/hasbro/tickets", form);
            onCreated(data.ticket);
        } catch (err) {
            setError(apiError(err, "Could not create the ticket."));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog title={staff ? "New ticket" : "Raise a ticket"} onClose={onClose} width="max-w-2xl"
            footer={<>
                <Button variant="transparent" onClick={onClose}>Cancel</Button>
                <Button variant="emphasized" onClick={submit} disabled={saving}>{saving ? "Submitting…" : "Submit ticket"}</Button>
            </>}>
            {error && <MessageStrip type="error">{error}</MessageStrip>}
            {staff && (
                <Field label="Customer">
                    <select value={form.customerId} onChange={set("customerId")} className={inputCls}>
                        <option value="">Choose a customer…</option>
                        {customers.filter(c => c.status === "active").map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </Field>
            )}
            <div>
                <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">What is it about?</span>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
                    {CATEGORIES.map(c => (
                        <button key={c.id} type="button" onClick={() => setForm(f => ({ ...f, category: c.id }))}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-semibold text-left transition ${form.category === c.id
                                ? "border-[#0087C9] bg-[#0087C9]/10 text-[#0065A0] dark:text-[#6FD0F5]"
                                : "border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:border-slate-300"}`}>
                            <c.icon size={14} className="shrink-0" /> {c.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                    <Field label="Subject"><input value={form.subject} onChange={set("subject")} maxLength={150} className={inputCls} placeholder="e.g. Missing POD for PO 4500123" /></Field>
                </div>
                <Field label="PO / order / ASN"><input value={form.reference} onChange={set("reference")} maxLength={60} className={`${inputCls} font-mono`} placeholder="Optional" /></Field>
            </div>
            <Field label="Details">
                <textarea value={form.description} onChange={set("description")} maxLength={5000} rows={4} className={`${inputCls} h-auto py-2 resize-y`}
                    placeholder="What happened, and what do you need from Hasbro?" />
            </Field>
            <div className="space-y-3">
                <div>
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">How much is affected?</span>
                    <div className="mt-1"><ChoiceCards options={IMPACT} value={form.impact} onChange={v => setForm(f => ({ ...f, impact: v }))} /></div>
                </div>
                <div>
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">How soon do you need it?</span>
                    <div className="mt-1"><ChoiceCards options={URGENCY} value={form.urgency} onChange={v => setForm(f => ({ ...f, urgency: v }))} /></div>
                </div>
                <MessageStrip>
                    This will be <span className="font-semibold">{PRIORITIES[priority].label}</span> — Hasbro aims to respond within{" "}
                    <span className="font-semibold">{sla.response} hour{sla.response !== 1 ? "s" : ""}</span> and resolve within{" "}
                    <span className="font-semibold">{sla.resolution < 48 ? `${sla.resolution} hours` : `${Math.round(sla.resolution / 24)} days`}</span>.
                </MessageStrip>
            </div>
        </Dialog>
    );
}

function TicketObjectPage({ id, staff, onBack }) {
    const { userName } = useCurrentUser();
    const [data, setData] = useState(null);
    const [staffList, setStaffList] = useState([]);
    const [error, setError] = useState("");
    const [reply, setReply] = useState("");
    const [internal, setInternal] = useState(false);
    const [rating, setRating] = useState(0);
    const [ratingNote, setRatingNote] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/api/hasbro/tickets/${id}`);
            setData(data);
        } catch (err) {
            setError(apiError(err, "Could not load the ticket."));
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { if (staff) api.get("/api/hasbro/staff").then(r => setStaffList(r.data.staff || [])).catch(() => {}); }, [staff]);

    const act = fn => async () => {
        setBusy(true);
        setError("");
        try { await fn(); await load(); }
        catch (err) { setError(apiError(err, "Something went wrong.")); }
        finally { setBusy(false); }
    };
    const send = act(async () => {
        await api.post(`/api/hasbro/tickets/${id}/messages`, { body: reply, internal: staff && internal });
        setReply("");
    });
    const update = patch => act(() => api.patch(`/api/hasbro/tickets/${id}`, patch))();
    const customer = (action, extra = {}) => act(() => api.post(`/api/hasbro/tickets/${id}/customer-action`, { action, ...extra }))();

    const t = data?.ticket;
    const cat = t && categoryOf(t.category);
    const done = t && DONE_STATUSES.has(t.status);

    return (
        <div className="max-w-6xl mx-auto px-1 sm:px-2 py-4 space-y-5">
            {error && <MessageStrip type="error">{error}</MessageStrip>}
            {!t ? (!error && <div className={`${surface} h-40 animate-pulse`} />) : (<>
                <PageHeader
                    breadcrumb={[{ label: staff ? "Customer Tickets" : "My Tickets", onClick: onBack }, { label: ticketNumber(t.number) }]}
                    title={t.subject}
                    actions={<div className="flex items-center gap-2 flex-wrap">
                        <PriorityBadge p={t.priority} />
                        <TicketStatus status={t.status} customer={!staff} />
                        {!staff && !done && <Button variant="negative" icon={XCircle} disabled={busy} onClick={() => window.confirm("Cancel this request? Hasbro will stop working on it.") && customer("cancel")}>Cancel request</Button>}
                    </div>}
                >
                    <StageTracker t={t} customer={!staff} />
                </PageHeader>

                {/* Customer: confirm the fix, reopen, rate */}
                {!staff && t.status === "resolved" && (
                    <MessageStrip type="success">
                        <p className="font-semibold">Hasbro has marked this as resolved. Has it fixed your problem?</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                            <Button variant="emphasized" icon={CheckCircle2} disabled={busy} onClick={() => customer("close")}>Yes, close the ticket</Button>
                            <Button icon={RotateCcw} disabled={busy} onClick={() => customer("reopen")}>No, reopen it</Button>
                        </div>
                    </MessageStrip>
                )}
                {!staff && t.status === "closed" && (
                    <MessageStrip>This ticket is closed. Something still wrong? <button className="underline font-semibold" disabled={busy} onClick={() => customer("reopen")}>Reopen it</button> (within 7 days of closing) or raise a new ticket.</MessageStrip>
                )}
                {!staff && ["resolved", "closed"].includes(t.status) && !t.csat && (
                    <div className={`${surface} p-5 space-y-3`}>
                        <p className="text-sm font-bold text-[#1D2D3E] dark:text-white">How did we do?</p>
                        <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map(n => (
                                <button key={n} onClick={() => setRating(n)} title={`${n} of 5`}>
                                    <Star size={26} className={n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300 dark:text-slate-600"} />
                                </button>
                            ))}
                        </div>
                        <textarea value={ratingNote} onChange={e => setRatingNote(e.target.value)} maxLength={1000} rows={2} placeholder="Anything you'd like to add? (optional)" className={`${inputCls} h-auto py-2`} />
                        <Button variant="emphasized" disabled={!rating || busy} onClick={() => customer("rate", { rating, comment: ratingNote })}>Send rating</Button>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5 items-start">
                    {/* Activity stream */}
                    <TableCard title="Activity" count={data.messages.filter(m => m.authorType !== "system").length}>
                        <ol className="px-5 py-4 space-y-4">
                            {data.messages.map(m => <ActivityItem key={m.id} m={m} t={t} />)}
                        </ol>
                        {!staff && ["closed", "cancelled"].includes(t.status) ? null : (
                            <form onSubmit={e => { e.preventDefault(); if (reply.trim()) send(); }}
                                className="flex gap-3 items-start px-5 py-4 border-t border-slate-100 dark:border-white/[0.06] bg-slate-50/60 dark:bg-white/[0.02]">
                                <Avatar name={userName || "Me"} size={34} />
                                <div className="flex-1 space-y-2">
                                    {staff && (
                                        <div className="flex gap-1 text-xs">
                                            {[[false, "Reply to customer"], [true, "Internal work note"]].map(([v, label]) => (
                                                <button key={label} type="button" onClick={() => setInternal(v)}
                                                    className={`px-2.5 py-1 rounded-md font-semibold ${internal === v ? (v ? "bg-amber-100 text-amber-800 dark:bg-amber-400/15 dark:text-amber-300" : "bg-[#0087C9]/15 text-[#0065A0] dark:text-[#6FD0F5]") : "text-slate-500 hover:text-slate-700"}`}>
                                                    {v && <Lock size={11} className="inline mr-1 -mt-0.5" />}{label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    <textarea value={reply} onChange={e => setReply(e.target.value)} maxLength={5000} rows={3}
                                        onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && reply.trim()) send(); }}
                                        placeholder={staff ? (internal ? "Note for the Hasbro team — the customer won't see this" : "Reply to the customer…") : "Add a message for Hasbro…"}
                                        className={`${inputCls} h-auto py-2 resize-y ${internal ? "border-amber-300 dark:border-amber-400/40" : ""}`} />
                                    <div className="flex items-center justify-between">
                                        <span className="text-[11px] text-slate-400">⌘/Ctrl + Enter to send</span>
                                        <Button type="submit" variant="emphasized" icon={internal ? Lock : Send} disabled={busy || !reply.trim()}>{internal ? "Add work note" : "Send"}</Button>
                                    </div>
                                </div>
                            </form>
                        )}
                    </TableCard>

                    {/* Details panel */}
                    <div className={`${surface} p-5 space-y-4`}>
                        <Facet label="Assigned to">
                            {staff ? (
                                <div className="space-y-2">
                                    <select value={t.assignedTo?.uid || ""} disabled={busy} onChange={e => update({ assigneeUid: e.target.value || null })} className={`${inputCls} h-8`}>
                                        <option value="">— {t.assignmentGroup} queue —</option>
                                        {staffList.map(s => <option key={s.uid} value={s.uid}>{s.name}</option>)}
                                    </select>
                                    {!t.assignedTo && <Button icon={UserCheck} disabled={busy} onClick={() => update({ assignToMe: true })} className="w-full">Assign to me</Button>}
                                </div>
                            ) : t.assignedTo
                                ? <span className="flex items-center gap-2"><Avatar name={t.assignedTo.name} size={26} />{t.assignedTo.name}</span>
                                : <span className="text-slate-500">Waiting for someone in {t.assignmentGroup} to pick it up</span>}
                            <span className="block text-xs text-slate-400 mt-1">Group: {t.assignmentGroup}</span>
                        </Facet>
                        {staff && (
                            <Facet label="Status">
                                <select value={t.status} disabled={busy} onChange={e => update({ status: e.target.value })} className={`${inputCls} h-8`}>
                                    {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                                </select>
                            </Facet>
                        )}
                        <Facet label="Priority">
                            <PriorityBadge p={t.priority} />
                            {staff ? (
                                <div className="grid grid-cols-2 gap-2 mt-2">
                                    <SelectField label="Impact" value={String(t.impact)} onChange={v => update({ impact: Number(v) })}>
                                        {IMPACT.map(o => <option key={o.value} value={o.value}>{o.value} — {o.label}</option>)}
                                    </SelectField>
                                    <SelectField label="Urgency" value={String(t.urgency)} onChange={v => update({ urgency: Number(v) })}>
                                        {URGENCY.map(o => <option key={o.value} value={o.value}>{o.value} — {o.label}</option>)}
                                    </SelectField>
                                </div>
                            ) : (
                                <span className="block text-xs text-slate-400 mt-1">{IMPACT.find(o => o.value === t.impact)?.label} · {URGENCY.find(o => o.value === t.urgency)?.label.toLowerCase()}</span>
                            )}
                        </Facet>
                        <Facet label="Service targets">
                            <SlaLine label="First response" clock={t.sla?.response} />
                            <SlaLine label="Resolution" clock={t.sla?.resolution} />
                        </Facet>
                        <Facet label="Details">
                            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs">
                                {staff && <><dt className="text-slate-400">Customer</dt><dd className="flex items-center gap-1.5"><Avatar name={t.customerName} size={18} square />{t.customerName}</dd></>}
                                <dt className="text-slate-400">Category</dt><dd className="flex items-center gap-1.5"><cat.icon size={12} className="text-[#0087C9]" />{cat.label}</dd>
                                <dt className="text-slate-400">PO / order</dt><dd className="font-mono">{t.reference || "—"}</dd>
                                <dt className="text-slate-400">Opened by</dt><dd>{t.createdBy?.name}</dd>
                                <dt className="text-slate-400">Opened</dt><dd>{fmtDateTime(t.createdAt)}</dd>
                                {t.resolvedAt && <><dt className="text-slate-400">Resolved</dt><dd>{fmtDateTime(t.resolvedAt)}</dd></>}
                                {t.closedAt && <><dt className="text-slate-400">{t.status === "cancelled" ? "Cancelled" : "Closed"}</dt><dd>{fmtDateTime(t.closedAt)}</dd></>}
                            </dl>
                        </Facet>
                        {t.csat && (
                            <Facet label="Customer rating">
                                <span className="flex gap-0.5">{[1, 2, 3, 4, 5].map(n => <Star key={n} size={16} className={n <= t.csat.rating ? "fill-amber-400 text-amber-400" : "text-slate-300 dark:text-slate-600"} />)}</span>
                                {t.csat.comment && <span className="block text-xs text-slate-500 mt-1">“{t.csat.comment}”</span>}
                            </Facet>
                        )}
                    </div>
                </div>
            </>)}
        </div>
    );
}

// Submitted → Assigned → In progress → Resolved → Closed, with % complete
function StageTracker({ t, customer }) {
    const cancelled = t.status === "cancelled";
    return (
        <div className={`${surface} p-5`}>
            <div className="flex items-center justify-between mb-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Progress</p>
                <p className="text-xs font-semibold text-[#0065A0] dark:text-[#6FD0F5]">{cancelled ? "Cancelled" : `${t.completion}% complete`}</p>
            </div>
            <ol className="flex items-start">
                {STAGES.map((label, i) => {
                    const n = i + 1;
                    const state = cancelled ? (n === 1 ? "done" : "todo") : n < t.stage || (n === 5 && t.stage === 5) ? "done" : n === t.stage ? "current" : "todo";
                    return (
                        <li key={label} className="flex-1 flex flex-col items-center text-center relative">
                            {i > 0 && <span className={`absolute top-[9px] right-1/2 w-full h-0.5 ${state !== "todo" ? "bg-[#0087C9]" : "bg-slate-200 dark:bg-white/10"}`} />}
                            <span className={`relative w-5 h-5 rounded-full border-2 grid place-items-center ${state === "done" ? "bg-[#0087C9] border-[#0087C9]" : state === "current" ? "bg-white dark:bg-[#18212C] border-[#0087C9] ring-4 ring-[#0087C9]/20" : "bg-white dark:bg-[#18212C] border-slate-300 dark:border-white/20"}`}>
                                {state === "done" && <CheckCircle2 size={12} className="text-white" />}
                            </span>
                            <span className={`mt-2 text-xs ${state === "todo" ? "text-slate-400" : "font-semibold text-[#1D2D3E] dark:text-white"}`}>{label}</span>
                        </li>
                    );
                })}
            </ol>
            {t.status === "awaiting_customer" && (
                <p className="mt-4 text-xs text-violet-700 dark:text-violet-300 flex items-center gap-1.5"><MessageSquareReply size={13} />
                    {customer ? "On hold — Hasbro is waiting for your reply below." : "On hold — waiting for the customer to reply."}</p>
            )}
        </div>
    );
}

function ActivityItem({ m, t }) {
    if (m.authorType === "system") {
        return (
            <li className="flex gap-3 items-center pl-2">
                <span className="w-6 grid place-items-center"><Activity size={13} className="text-slate-400" /></span>
                <span className="text-xs text-slate-500 dark:text-slate-400">{m.body} · <span title={fmtDateTime(m.at)}>{relTime(m.at)}</span>{m.author?.name && ` · ${m.author.name}`}</span>
            </li>
        );
    }
    const fromHasbro = m.authorType === "staff";
    return (
        <li className="flex gap-3">
            <Avatar name={m.author?.name || "?"} size={34} />
            <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="text-sm font-bold text-[#1D2D3E] dark:text-white">{m.author?.name}</span>
                    {m.internal
                        ? <ObjectStatus tone="critical" icon={Lock}>Work note · staff only</ObjectStatus>
                        : <ObjectStatus tone={fromHasbro ? "information" : "neutral"}>{fromHasbro ? "Hasbro" : t.customerName}</ObjectStatus>}
                    <span className="text-xs text-slate-400" title={fmtDateTime(m.at)}>{relTime(m.at)}</span>
                </div>
                <div className={`mt-1.5 rounded-2xl rounded-tl-sm px-4 py-3 text-sm whitespace-pre-wrap break-words ${m.internal
                    ? "bg-amber-50 text-amber-950 border border-amber-200 dark:bg-amber-400/10 dark:text-amber-100 dark:border-amber-400/20"
                    : fromHasbro ? "bg-[#E5F4FC] text-[#0B2E4A] dark:bg-[#0087C9]/15 dark:text-slate-100"
                    : "bg-slate-50 text-slate-800 dark:bg-white/[0.04] dark:text-slate-200"}`}>
                    {m.body}
                </div>
            </div>
        </li>
    );
}

function SlaLine({ label, clock }) {
    const s = slaState(clock);
    return (
        <div className="flex items-center justify-between gap-2 text-xs py-0.5">
            <span className="text-slate-500 dark:text-slate-400">{label}</span>
            <span className="text-right">
                <ObjectStatus tone={s.tone}>{s.text}</ObjectStatus>
                {clock?.due && <span className="block text-[11px] text-slate-400 mt-0.5">by {fmtDateTime(clock.due)}</span>}
            </span>
        </div>
    );
}

function Facet({ label, children }) {
    return (
        <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">{label}</p>
            <div className="text-sm text-[#1D2D3E] dark:text-slate-100">{children}</div>
        </div>
    );
}
